// nhs-login-errors.data.ts
//
// NHS Login and SSO. The OAuth codes arrive from the provider's callback and are
// looked up by code at runtime, so some carry metadata without copy of their own.

import { ErrorEntry } from './error-metadata.types';

export const nhsLoginErrors = {
  'unknown-error-nhs-param': {
    message:
      'There was an issue with NHS login. This may be a temporary problem. \n' +
      'Go back and try logging in again. If you cannot log in, try again later.',
    title: 'There is a problem',
    messageNhsAppWeb:
      'There was an issue with NHS login. This may be a temporary problem. \n' +
      'Close this tab and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'nhs-sso-generic': {
    message:
      'There was an issue with NHS login. This may be a temporary problem. \n' +
      'Go back and try logging in again. If you cannot log in, try again later.',
    title: 'There is a problem',
    messageNhsAppWeb: 'There was an issue with NHS login. This may be a temporary problem.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'medicus-sso-generic': {
    message:
      'This may be a temporary problem. Go back and try logging in again. If you cannot log in, try again later.',
    title: 'There is a problem with the service',
    messageNhsAppWeb:
      'This may be a temporary problem. Close this tab and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'nhs-app-sso-session-expired': {
    message:
      'Your sign in has expired. This happens if the page was reloaded, or if too long passed while you were signing in.\n\n' +
      'Go back to the NHS App and start your request again.',
    title: 'Your sign in has expired',
    messageNhsAppWeb:
      'Your sign in has expired. This happens if the page was reloaded, or if too long passed while you were signing in.\n\n' +
      'Close this tab and start your request again from the NHS App.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'sso-user-session-invalid': {
    message:
      'There was an issue with NHS login. This may be a temporary problem. \n\n' +
      'Go back and try logging in again. If you cannot log in, try again later.',
    title: 'There is a problem with the service',
    messageNhsAppWeb:
      'This may be a temporary problem. Go back and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'nhs-login-logged-in-patient-not-found': {
    message:
      'It was not possible to match the NHS login user to a patient record at this practice.',
    title: 'Your record could not be found',
  },
  'nhs-login-user-verification-level-too-low': {
    message:
      'It’s not possible to submit your request online with NHS login because your identity isn’t verified.\n\n' +
      'To learn how to verify your identity, visit the NHS login help centre.\n\n' +
      'You can submit a request now by entering your personal details instead.',
    title: 'Your NHS login identity is not verified',
  },
  'nhs-login-sso-user-verification-level-too-low': {
    message:
      'It’s not possible to submit your request online with NHS login because your identity isn’t verified.',
    title: 'Your NHS login identity is not verified',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
          showNHSAppVerificationDetails: true,
        },
      },
    ],
  },
  access_denied: {
    message:
      'The service provider needs your NHS login information to verify your identity.\n' +
      'To continue, go back and agree to share your NHS login information. ',
    title: 'You cannot continue without sharing your information',
    messageNhsAppWeb:
      'The service provider needs your NHS login information to verify your identity.\n' +
      'To continue, close this tab then go back and agree to share your NHS login information. ',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'consent-not-given-nhs': {
    message:
      'The service provider needs your NHS login information to verify your identity.\n\n' +
      'To continue, go back and agree to share your NHS login information.',
    title: 'You cannot continue without sharing your information',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },
  consent_required: {
    message:
      'The service provider needs your NHS login information to verify your identity.\n\n' +
      'To continue, go back and agree to share your NHS login information.',
    title: 'You cannot continue without sharing your information',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },
  identity_proofing_level: {
    message:
      'It’s not possible to submit your request online with NHS login because your identity isn’t verified. ',
    title: 'Your NHS login identity is not verified',
    messageNhsAppWeb:
      'It’s not possible to submit your request online with NHS login because your identity isn’t verified. ',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
          showNHSAppVerificationDetails: true,
        },
      },
    ],
  },
  invalid_request: {
    title: 'There was a problem with NHS login',
    redirectToError: true,
    details: 'Error details',
    route: '',
  },
  unauthorized_client: {
    title: 'There was a problem with NHS login',
  },
  'unknown-error-authenticating-nhs-login-with-client': {
    title: 'There is a problem with NHS login',
  },
} satisfies Record<string, ErrorEntry>;
